import type { ComponentProps, ReactNode } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export type ListConsolePageProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  error?: ReactNode;
  /** When true (default), page fills viewport for AG Grid modules. */
  fill?: boolean;
  /** Omit the standalone console header card (title lives in page content instead). */
  hideHeader?: boolean;
  className?: string;
  children: ReactNode;
};

/** Header / toolbar primary actions — shared console button classes. */
export function ConsoleActionButton({
  variant = 'default',
  className,
  size = 'sm',
  ...props
}: ComponentProps<typeof Button> & { variant?: 'default' | 'outline' }) {
  return (
    <Button
      size={size}
      variant={variant === 'outline' ? 'outline' : 'default'}
      className={cn(
        'tm-console-action-btn gap-1.5',
        variant === 'outline' && 'tm-console-action-btn--outline',
        className,
      )}
      {...props}
    />
  );
}

/** Shared light console page header (list modules, profile, analytics). */
export function ConsolePageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="tm-console-header">
      <div className="tm-console-header-row">
        <div className="tm-console-header-copy">
          <h1 className="tm-console-title">{title}</h1>
          {subtitle ? <p className="tm-console-sub">{subtitle}</p> : null}
        </div>
        {actions ? (
          <div className="tm-console-header-actions">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}

/** TM-style list module shell (Merchants, Acquirers, Operations grids). */
export function ListConsolePage({
  title,
  subtitle,
  actions,
  error,
  fill = true,
  hideHeader = false,
  className,
  children,
}: ListConsolePageProps) {
  return (
    <div className={cn('tm-page', fill ? 'dh-page-fill' : 'dh-page', className)}>
      {!hideHeader ? (
        <ConsolePageHeader title={title} subtitle={subtitle} actions={actions} />
      ) : null}
      {error ? <div className="tm-error-banner">{error}</div> : null}
      {children}
    </div>
  );
}

type ListConsoleGridSectionProps = {
  toolbarLeft?: ReactNode;
  toolbarRight?: ReactNode;
  /** Hide the toolbar row (e.g. Payment Routing → Simulator). */
  hideToolbar?: boolean;
  filterBar?: ReactNode;
  /** Content between toolbar card and grid card (status strips, hints). */
  between?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function ListConsoleGridSection({
  toolbarLeft,
  toolbarRight,
  hideToolbar = false,
  filterBar,
  between,
  children,
  className,
}: ListConsoleGridSectionProps) {
  const showToolbar = !hideToolbar && (toolbarLeft != null || toolbarRight != null);

  return (
    <div className={cn('dh-table-section tm-table-sections', className)}>
      {showToolbar ? (
        <Card className="tm-table-card tm-table-toolbar-card">
          <div className="tm-table-panel">
            <div className="tm-table-toolbar-row">
              {toolbarLeft}
              {toolbarRight != null ? (
                <div className="tm-table-toolbar-actions">{toolbarRight}</div>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}
      {between}
      <Card className="tm-table-card tm-table-grid-card overflow-hidden">
        {filterBar}
        {children}
      </Card>
    </div>
  );
}

export const LIST_CONSOLE_AG_GRID_CLASS = 'tm-ag-grid';
